import React, { useEffect, useState } from "react";
import { ButtonType } from "@components/atoms/Button/types";
import Popup from "@components/molecules/PopUp";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useTranslation } from "react-i18next";
import { messageType } from "@components/molecules/PopUp/types";
import { useCancelRenewal, useGetUser } from "@apis/user";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getDaysLeft } from "@scenes/more/MoreScreen/utils";

type CancelRenewalPopupType = {
  isOpen: boolean;
  onClose: () => void;
};

const CancelRenewalPopup = (props: CancelRenewalPopupType) => {
  const { isOpen = false, onClose } = props;
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const cancelRenewal = useCancelRenewal();
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [userId, setUserId] = useState(userInfo?.user?.id);
  const {} = useGetUser(userId, setUserId);
  useEffect(() => {
    const _subscriptionId = userInfo.getUpcomingSubscription()?.subscription_id;
    if (_subscriptionId) {
      setSubscriptionId(_subscriptionId);
    }
  }, [isOpen]);

  const popupStyles = {
    accept: {
      title: t("manageRenewal.popUp.title.text", "Cancel Upcoming Renewal?"),
      showComments: false,
      primaryButtonText: t(
        "manageRenewal.popUp.button.keepRenewal.text",
        "Keep Renewal"
      ),
      secondaryButtonText: t(
        "manageRenewal.popUp.button.cancelRenewal.text",
        "Cancel Renewal"
      ),
      primaryButtonOnPress: () => {
        onClose();
      },
      secondaryButtonOnPress: () => {
        handleCancelRenewal();
      },
    },
  };

  const handleCancelRenewal = () => {
    if (subscriptionId) {
      cancelRenewal.mutate(
        {
          subscription_id: userInfo.getUpcomingSubscription()?.subscription_id,
        },
        {
          onSuccess(data: any) {
            showToast(
              undefined,
              data.data.message,
              ToastTypes.SUCCESS,
              true,
              true
            );
            setTimeout(() => {
              setUserId(userInfo?.user?.id);
            }, 4000);
            onClose?.();
          },
          onError(errorData: any) {
            const errorMessage =
              errorData?.response?.data?.response?.header?.message;
            if (errorMessage) {
              showToast(undefined, errorMessage, ToastTypes.ERROR, true, true);
            }
          },
        }
      );
    } else {
      showToast(
        undefined,
        t("manageRenewal.popUp.error.text", "Subscription ID not found"),
        ToastTypes.ERROR,
        true,
        true
      );
    }
  };

  return (
    <Popup
      type={messageType.Warning}
      styleKey="Body-R"
      title={popupStyles?.accept?.title}
      buttons={[
        {
          type: ButtonType.primary,
          title: popupStyles?.accept?.primaryButtonText,
          onPressAction: popupStyles?.accept?.primaryButtonOnPress,
        },
        {
          type: ButtonType.secondary,
          title: popupStyles?.accept?.secondaryButtonText,
          onPressAction: popupStyles?.accept?.secondaryButtonOnPress,
          loading: !!cancelRenewal.isPending,
          disabled: !!cancelRenewal.isPending,
        },
      ]}
      HtmlSource={{
        html: t(
          "manageRenewal.popUp.bodyTitle.text",
          `<div>Are you sure you want to cancel your upcoming renewal? Your current membership will remain active through <b>{{expiry_date}}</b>.</div>
          <div style="height: 16px;"></div>
          <div>After that, <b>your users will move to Limited Access</b> unless a new plan is purchased.</div>`,
          {
            expiry_date: userInfo.planExpiryDetails()?.expiry_date
              ? getDaysLeft(userInfo.planExpiryDetails()?.expiry_date)
                  ?.expiryDate ?? ""
              : "",
          }
        ),
      }}
    />
  );
};

export default CancelRenewalPopup;
